import { useCallback, useState } from 'react';
import * as Yup from 'yup';

export type TEventTarget = {
    target: {
        value: any;
        name: string;
    };
};

export interface IObjectAutocomplete {
    ID: string;
    Number: string;
    InsurerName: string;
}

export interface IAutocomplete {
    ID: string;
    Name: string;
}

export function useForm<T>(
    inputValues: any,
    validationSchema?: Yup.Schema<T>
) {
    const [formData, setFormData] = useState<any>(inputValues);
    const [errors, setErrors] = useState<{ [key: string]: string | null }>({});

    interface IHandleChangeInput {
        event: React.ChangeEvent<HTMLInputElement>;
        index?: number;
        changeName?: string;
        indexObj?: number;
    }

    const updateNestedObject = (obj: any, path: string[], value: any): any => {
        if (path.length === 1) {
            return { ...obj, [path[0]]: value };
        }

        const [currentKey, ...restPath] = path;
        const currentValue = obj[currentKey];

        if (Array.isArray(currentValue)) {
            const index = parseInt(restPath[0], 10);
            if (!isNaN(index)) {
                const newArray = [...currentValue];
                if (restPath.length === 1) {
                    newArray[index] = value;
                } else {
                    newArray[index] = updateNestedObject(newArray[index], restPath.slice(1), value);
                }
                return {
                    ...obj,
                    [currentKey]: newArray,
                };
            }
        }
        return {
            ...obj,
            [currentKey]: updateNestedObject(currentValue, restPath, value),
        };
    };

    const handleChangeInput = ({ event }: IHandleChangeInput) => {
        const { value, name } = event.target;
        const path = name.split('.');
        setFormData((prevFormData: any) => {
            return updateNestedObject(prevFormData, path, value);
        });
    };

    const handleBlurInput = async (event: TEventTarget) => {
        const { name } = event.target;
        if (validationSchema) {
            await validationSchema
                .validateAt(name, formData)
                .then(() => {
                    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
                })
                .catch((err) => {
                    setErrors((prevErrors) => ({ ...prevErrors, [name]: err.message }));
                });
        }
    };
    const handleDateChange = (newValue: Date | null, name: string) => {
        if (!newValue) {
            setFormData((prev: any) => ({
                ...prev,
                [name]: ''
            }));
            return;
        }

        const day = newValue.getDate().toString().padStart(2, '0');
        const month = (newValue.getMonth() + 1).toString().padStart(2, '0');
        const year = newValue.getFullYear();
        const formattedDate = `${day}.${month}.${year}`;

        setFormData((prev: any) => ({
            ...prev,
            [name]: formattedDate
        }));
    };

    const handleAutocompeteChange = (
        newValue: IObjectAutocomplete | IObjectAutocomplete[] | null,
        name: string,
        multiple: boolean
    ) => {
        if (newValue) {
            const path = name.split('.');
            let value: string | string[];

            if (multiple && Array.isArray(newValue)) {
                value = newValue.map((item) => item.ID);
            } else if (!multiple && !Array.isArray(newValue)) {
                value = (newValue as IObjectAutocomplete).ID;
            } else {
                value = multiple ? [''] : '';
            }

            setFormData((prevFormData: any) => updateNestedObject(prevFormData, path, value));
        } else {
            setFormData((prevFormData: any) => updateNestedObject(prevFormData, name.split('.'), multiple ? [''] : ''));
        }
    };
    const handleAutocompeteChangeArray = (
        value: string,
        name: any,
    ) => {
        const path = name.split('.');
        setFormData((prevFormData: any) =>
            updateNestedObject(prevFormData, path, value)
        );
    };


    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const newFiles = input.files;
        if (!newFiles) return;

        const filesArray = Array.from(newFiles);
        const newFileItems: FileItem[] = [];

        for (const file of filesArray) {
            try {
                const base64Data = await convertFileToBase64(file);
                newFileItems.push({
                    Name: file.name,
                    DataBase64: base64Data
                });
            } catch (error) {
                console.error('Ошибка при чтении файла:', error);
            }
        }

        setFormData((prev: any) => ({
            ...prev,
            Files: [...(prev.Files || []), ...newFileItems]
        }));
        input.value = '';
    };

    const handleRemoveFile = (index: number) => {
        setFormData((prev: any) => {
            const updatedFiles = [...(prev.Files || [])];
            updatedFiles.splice(index, 1);
            return {
                ...prev,
                Files: updatedFiles
            };
        });
    };

    const handleRemoveFilesAdd = (index: number) => {
        setFormData((prev: any) => {
            const updatedFiles = [...(prev.FilesAdd || [])];
            updatedFiles.splice(index, 1);
            return {
                ...prev,
                FilesAdd: updatedFiles
            };
        });
    };


    const handleDateInput = useCallback((value: string, name: string) => {
        const path = name.split('.');
        setFormData((prev: any) => updateNestedObject(prev, path, value));
    }, []);


    return {
        formData,
        setFormData,
        errors,
        setErrors,
        handleAutocompeteChange,
        handleChangeInput,
        handleBlurInput,
        handleDateChange,
        handleAutocompeteChangeArray,
        handleRemoveFile,
        handleFileChange,
        handleDateInput,
        handleRemoveFilesAdd
    };
}
type FileItem = {
    Name: string;
    DataBase64: string;
};